/**
 * Controllo di gestione › Piani riclassificati (08/10/2026): the plans of
 * the management account, not the accounts of the accountant. The plan Base
 * is matched to the registry, the invoices and their lines; any other plan
 * groups the items of the Base in its own way. An item is analytic, or an
 * aggregate with a formula of codes added and taken away («RL-CVC-CVP-CFS»).
 * Amounts are positive; the sign of an item says whether it adds to its group
 * or takes away from it, and the formulas repeat it.
 */
import { z } from 'zod';
import { optionalText, requiredText } from './validation.js';
export const RECLASS_SIGNS = ['+', '-'];
/** The code of the plan matched to registry, invoices and lines. */
export const BASE_PLAN_CODE = 'BASE';
const itemCode = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{1,12}$/, { message: 'Codice: lettere e cifre, al massimo 12' });
export const reclassItemSchema = z.object({
    code: itemCode,
    sign: z.enum(RECLASS_SIGNS),
    description: requiredText(200),
    /** Null for an analytic item. */
    formula: optionalText(500).transform((value) => (value ? value.replace(/\s+/g, '') : null)),
    /** For a plan other than the Base: the analytic items of the Base it gathers. */
    baseItems: z.array(itemCode).max(200).default([]),
    active: z.boolean().default(true),
});
export const reclassPlanSchema = z.object({
    name: requiredText(80),
    /** A new plan may start as a copy of another. */
    copyFrom: z.uuid().nullable().default(null),
});
export const reclassOrderSchema = z.object({ ids: z.array(z.uuid()).min(1).max(500) });
export const reclassImportSchema = z.object({
    csv: z.string().min(1).max(200_000),
    dryRun: z.boolean().default(false),
});
/** «RL-CVC-CVP» → the codes with their sign; null when the formula is not well written. */
export function formulaTerms(formula) {
    const text = formula.replace(/\s+/g, '');
    if (!/^[+-]?[A-Za-z0-9]+([+-][A-Za-z0-9]+)*$/.test(text))
        return null;
    return [...text.matchAll(/([+-]?)([A-Za-z0-9]+)/g)].map((match) => ({
        sign: match[1] === '-' ? '-' : '+',
        code: match[2],
    }));
}
/**
 * What is wrong in a plan, item by item: codes twice, formulas badly
 * written, codes that do not exist, aggregates that depend on themselves,
 * analytic items no aggregate counts. Empty when the plan is right.
 */
export function reclassProblems(items) {
    const problems = [];
    const byCode = new Map();
    for (const item of items) {
        if (byCode.has(item.code))
            problems.push({ code: item.code, message: 'codice ripetuto' });
        byCode.set(item.code, item);
    }
    const terms = new Map();
    for (const item of items) {
        if (!item.formula)
            continue;
        const found = formulaTerms(item.formula);
        if (!found) {
            problems.push({ code: item.code, message: `formula non valida «${item.formula}»` });
            continue;
        }
        terms.set(item.code, found);
        for (const term of found) {
            if (!byCode.has(term.code))
                problems.push({ code: item.code, message: `il codice ${term.code} non esiste` });
        }
    }
    // A formula that depends on itself, directly or through other aggregates.
    const state = new Map();
    const visit = (code, path) => {
        if (state.get(code) === 'done')
            return;
        if (state.get(code) === 'open') {
            problems.push({ code, message: `giro circolare: ${[...path, code].join(' → ')}` });
            return;
        }
        state.set(code, 'open');
        for (const term of terms.get(code) ?? [])
            visit(term.code, [...path, code]);
        state.set(code, 'done');
    };
    for (const code of terms.keys())
        visit(code, []);
    const counted = new Set([...terms.values()].flat().map((term) => term.code));
    for (const item of items) {
        if (!item.formula && item.active && !counted.has(item.code))
            problems.push({ code: item.code, message: 'nessun aggregato la conta' });
    }
    return problems;
}
/**
 * The amounts of every item of a plan from those of its analytic items, in
 * cents: an aggregate adds and takes away as its formula says.
 */
export function evaluateReclass(items, analytic) {
    const formulas = new Map(items.map((item) => [item.code, item.formula]));
    const result = new Map();
    const value = (code, depth) => {
        if (result.has(code))
            return result.get(code);
        const formula = formulas.get(code);
        if (!formula || depth > 50)
            return analytic.get(code) ?? 0;
        const total = (formulaTerms(formula) ?? []).reduce((sum, term) => sum + (term.sign === '-' ? -1 : 1) * value(term.code, depth + 1), 0);
        result.set(code, total);
        return total;
    };
    for (const item of items)
        value(item.code, 0);
    return result;
}
/** The analytic items of the Base a plan leaves out: a plan should show them all. */
export function unmappedBaseItems(base, plan) {
    const mapped = new Set(plan.flatMap((item) => item.baseItems));
    return base
        .filter((item) => !item.formula && item.active && !mapped.has(item.code))
        .map((item) => item.code);
}
/**
 * The CSV of a plan, as the Excel wrote it: «CONTO;SEGNO;DESCRIZIONE;FORMULA»,
 * a heading and a row per item. Rows that cannot be read are problems with
 * their number.
 */
export function readReclassCsv(text) {
    const lines = text.replace(new RegExp(`^${String.fromCharCode(0xfeff)}`), '').split(/\r?\n/);
    const items = [];
    const problems = [];
    lines.forEach((line, index) => {
        if (!line.trim())
            return;
        const cells = line.split(';').map((cell) => cell.trim());
        if (index === 0 && /^conto$/i.test(cells[0] ?? ''))
            return;
        const [code = '', sign = '', description = '', formula = ''] = cells;
        const where = `riga ${index + 1}`;
        if (!/^[A-Za-z0-9]{1,12}$/.test(code))
            return problems.push(`${where}: codice «${code}» non valido`);
        if (sign !== '+' && sign !== '-')
            return problems.push(`${where}: segno «${sign}» non valido`);
        if (!description)
            return problems.push(`${where}: manca la descrizione`);
        items.push({ code, sign, description: description.slice(0, 200), formula: formula || null });
    });
    return { items, problems };
}
/** The CSV of a plan, in the same layout. */
export function reclassCsv(items) {
    return [
        'CONTO;SEGNO;DESCRIZIONE;FORMULA',
        ...items.map((item) => [item.code, item.sign, item.description.replace(/;/g, ','), item.formula ?? ''].join(';')),
    ].join('\r\n');
}
/**
 * The analytic items a user chooses from (08/10/2026), each with its section:
 * the first aggregate of the plan, in its order, that sums it directly
 * («Ricavi», «Costi fissi di produzione»…). Inactive items are left out.
 */
export function reclassChoices(items) {
    const aggregates = items
        .filter((item) => item.formula)
        .map((item) => ({
        item,
        codes: new Set((formulaTerms(item.formula) ?? []).map((t) => t.code)),
    }));
    return items
        .filter((item) => !item.formula && item.active)
        .map((item) => {
        const section = aggregates.find((aggregate) => aggregate.codes.has(item.code))?.item ?? null;
        return {
            code: item.code,
            description: item.description,
            sectionCode: section?.code ?? null,
            section: section?.description ?? null,
        };
    });
}
//# sourceMappingURL=reclass.js.map